import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  CardActionArea,
  Box,
  Button,
  Chip,
  CircularProgress,
  Alert
} from '@mui/material';
import { LiveTv, Add, EmojiEvents } from '@mui/icons-material';
import { drawService } from '../services/drawService';
import { Draw } from '../types';
import { AppHeader } from '../components/AppHeader';
import { useNavigate } from 'react-router-dom';

const LiveDrawSelector: React.FC = () => {
  const navigate = useNavigate();
  const [pendingDraws, setPendingDraws] = useState<Draw[]>([]);
  const [inProgressDraws, setInProgressDraws] = useState<Draw[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDraws();
  }, []);

  const fetchDraws = async () => {
    try {
      console.log('🔍 Buscando sorteios...');
      const response = await drawService.getAllDraws();
      console.log('📊 Resposta completa da API:', response);
      console.log('🎯 Número total de sorteios:', response.length);

      // Só campanhas ativas no seletor ao vivo — evita apresentar sorteios
      // de campanhas antigas/encerradas que ainda estejam PENDING/IN_PROGRESS.
      const fromActiveCampaign = response.filter((draw) => {
        return !draw.campaign?.status || draw.campaign.status === 'ACTIVE';
      });
      
      const pending = fromActiveCampaign.filter(draw => {
        console.log(`🎲 Sorteio ${draw.id}: status = ${draw.status}`);
        return draw.status === 'PENDING';
      });

      const inProgress = fromActiveCampaign.filter(draw => {
        console.log(`🎲 Sorteio ${draw.id}: status = ${draw.status}`);
        return draw.status === 'IN_PROGRESS';
      });
      
      console.log('⏳ Sorteios pendentes encontrados:', pending.length);
      console.log('📋 Sorteios pendentes:', pending);
      console.log('🔄 Sorteios em progresso encontrados:', inProgress.length);
      console.log('📋 Sorteios em progresso:', inProgress);
      
      setPendingDraws(pending);
      setInProgressDraws(inProgress);
    } catch (error) {
      console.error('❌ Erro ao carregar sorteios:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <>
      <AppHeader 
        title="Sorteio ao Vivo" 
        subtitle="Realize sorteios com apresentação visual espetacular"
        icon={<EmojiEvents />}
      />

      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
          <Box>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: 'primary.main' }}>
              Escolha um Sorteio para Apresentar
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Selecione um sorteio pendente ou em progresso para iniciar/continuar a apresentação ao vivo
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => navigate('/draws/create')}
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1,
              fontWeight: 600,
              textTransform: 'none'
            }}
          >
            Novo Sorteio
          </Button>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 8 }}>
            <CircularProgress size={60} />
            <Typography variant="h6" color="text.secondary">
              Carregando sorteios...
            </Typography>
          </Box>
        ) : pendingDraws.length === 0 && inProgressDraws.length === 0 ? (
          <Card elevation={2} sx={{ p: 6, textAlign: 'center', borderRadius: 2 }}>
            <Box sx={{ mb: 3 }}>
              <EmojiEvents sx={{ fontSize: 64, color: 'warning.main', mb: 2 }} />
            </Box>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
              Nenhum Sorteio Disponível
            </Typography>
            <Typography variant="body1" color="text.secondary" gutterBottom sx={{ mb: 3 }}>
              Para usar o Sorteio ao Vivo, você precisa criar um sorteio primeiro.
              <br />
              Os sorteios precisam estar com status "Pendente" ou "Em Progresso" para aparecer aqui.
            </Typography>
            <Button
              variant="contained"
              size="large"
              startIcon={<Add />}
              onClick={() => navigate('/draws/create')}
              sx={{
                borderRadius: 2,
                px: 4,
                py: 1.5,
                fontWeight: 600,
                textTransform: 'none',
                fontSize: '1.1rem'
              }}
            >
              Criar Primeiro Sorteio
            </Button>
          </Card>
        ) : (
          <>
            <Alert 
              severity="info" 
              sx={{ mb: 3, borderRadius: 2 }}
              icon={<LiveTv />}
            >
              <Typography variant="body2">
                <strong>💡 Dica:</strong> Clique em "SORTEAR AO VIVO" para abrir a página de apresentação visual 
                em tela cheia, ideal para projetar em TVs ou monitores durante eventos.
              </Typography>
            </Alert>

            {/* Sorteios em Progresso */}
            {inProgressDraws.length > 0 && (
              <Box sx={{ mb: 4 }}>
                <Typography variant="h5" sx={{ mb: 2, fontWeight: 600, color: 'info.main' }}>
                  🎯 Sorteios em Progresso
                </Typography>
                <Box 
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: '1fr',
                      sm: 'repeat(2, 1fr)', 
                      md: 'repeat(3, 1fr)'
                    },
                    gap: 3
                  }}
                >
                  {inProgressDraws.map((draw) => (
                    <Card 
                      key={draw.id}
                      elevation={2}
                      sx={{ 
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        transition: 'transform 0.2s',
                        borderRadius: 2,
                        '&:hover': {
                          transform: 'scale(1.02)',
                          boxShadow: 4
                        }
                      }}
                    >
                      <CardContent sx={{ flexGrow: 1, p: 3 }}>
                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                          {draw.campaign?.name || draw.campaignId}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                          🏆 <strong style={{ marginLeft: 4, marginRight: 4 }}>Prêmio:</strong> 
                          {draw.prize?.name || draw.prizeId}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                          📅 <strong style={{ marginLeft: 4, marginRight: 4 }}>Data:</strong> 
                          {formatDate(draw.drawDate)}
                        </Typography>
                        <Typography variant="body2" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                          🎯 <strong style={{ marginLeft: 4, marginRight: 4 }}>Ganhadores:</strong> 
                          {draw.maxWinners} {draw.maxWinners === 1 ? 'ganhador' : 'ganhadores'}
                        </Typography>
                        <Box sx={{ mt: 2 }}>
                          <Chip 
                            label="🔄 Em Progresso" 
                            color="info" 
                            size="small"
                            sx={{ fontWeight: 600 }}
                          />
                        </Box>
                      </CardContent>
                      <CardActionArea>
                        <Button
                          fullWidth
                          variant="contained"
                          size="large"
                          startIcon={<LiveTv />}
                          onClick={() => navigate(`/draws/${draw.id}/live`)}
                          sx={{
                            py: 2,
                            background: 'linear-gradient(135deg, #4CAF50 0%, #2E7D32 100%)',
                            fontWeight: 700,
                            fontSize: '1.1rem',
                            textTransform: 'none',
                            '&:hover': {
                              background: 'linear-gradient(135deg, #2E7D32 0%, #4CAF50 100%)',
                              transform: 'scale(1.01)'
                            }
                          }}
                        >
                          🎉 CONTINUAR SORTEIO
                        </Button>
                      </CardActionArea>
                    </Card>
                  ))}
                </Box>
              </Box>
            )}

            {/* Sorteios Pendentes */}
            {pendingDraws.length > 0 && (
              <Box>
                <Typography variant="h5" sx={{ mb: 2, fontWeight: 600, color: 'warning.main' }}>
                  ⏳ Sorteios Pendentes
                </Typography>
                <Box 
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: '1fr',
                      sm: 'repeat(2, 1fr)', 
                      md: 'repeat(3, 1fr)'
                    },
                    gap: 3
                  }}
                >
                  {pendingDraws.map((draw) => (
                    <Card 
                      key={draw.id}
                      elevation={2}
                      sx={{ 
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        transition: 'transform 0.2s',
                        borderRadius: 2,
                        '&:hover': {
                          transform: 'scale(1.02)',
                          boxShadow: 4
                        }
                      }}
                    >
                      <CardContent sx={{ flexGrow: 1, p: 3 }}>
                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                          {draw.campaign?.name || draw.campaignId}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                          🏆 <strong style={{ marginLeft: 4, marginRight: 4 }}>Prêmio:</strong> 
                          {draw.prize?.name || draw.prizeId}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                          📅 <strong style={{ marginLeft: 4, marginRight: 4 }}>Data:</strong> 
                          {formatDate(draw.drawDate)}
                        </Typography>
                        <Typography variant="body2" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                          🎯 <strong style={{ marginLeft: 4, marginRight: 4 }}>Ganhadores:</strong> 
                          {draw.maxWinners} {draw.maxWinners === 1 ? 'ganhador' : 'ganhadores'}
                        </Typography>
                        <Box sx={{ mt: 2 }}>
                          <Chip 
                            label="⏳ Pendente" 
                            color="warning" 
                            size="small"
                            sx={{ fontWeight: 600 }}
                          />
                        </Box>
                      </CardContent>
                      <CardActionArea>
                        <Button
                          fullWidth
                          variant="contained"
                          size="large"
                          startIcon={<LiveTv />}
                          onClick={() => navigate(`/draws/${draw.id}/live`)}
                          sx={{
                            py: 2,
                            background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)',
                            fontWeight: 700,
                            fontSize: '1.1rem',
                            textTransform: 'none',
                            '&:hover': {
                              background: 'linear-gradient(135deg, #ee5a24 0%, #ff6b6b 100%)',
                              transform: 'scale(1.01)'
                            }
                          }}
                        >
                          🎉 SORTEAR AO VIVO
                        </Button>
                      </CardActionArea>
                    </Card>
                  ))}
                </Box>
              </Box>
            )}
          </>
        )}
      </Container>
    </>
  );
}; 
export default LiveDrawSelector;